import { APIFieldAttributes, ArdoqId } from '@ardoq/api-types';
import { SpiderChartViewModel, SpiderChartViewSettings } from './types';
import { getChartPoints, getChildComponentIds, getLegendRows } from './utils';
import { ItemsType, nodeLimitError } from '@ardoq/error-info-box';
import { dispatchAction } from '@ardoq/rxbeach';
import { bypassLimitAction } from './actions';
import type { I18nLocale } from '@ardoq/i18n';
import { formatNumber, type Locale } from '@ardoq/locale';
import { getNodeLimitErrorLabels } from 'locales/getErrorInfoBoxLabels';
import type { ComponentsState } from 'streams/components/components$';
import { SPIDER_COMPONENTS_LIMIT } from 'views/environmentals/viewDataLimits';

type BuildViewModelArgs = {
  componentId: ArdoqId;
  workspaceId: ArdoqId;
  focusedComponentId: ArdoqId | null;
  viewSettings: SpiderChartViewSettings;
  bypassLimit: boolean;
  i18nLocale: I18nLocale;
  locale: Locale;
  components: ComponentsState;
  numericFields: APIFieldAttributes[];
  includedComponentIds: Set<ArdoqId>;
};

export const buildViewModel = ({
  componentId,
  workspaceId,
  focusedComponentId,
  viewSettings,
  bypassLimit,
  i18nLocale,
  locale,
  components,
  numericFields,
  includedComponentIds,
}: BuildViewModelArgs): SpiderChartViewModel => {
  const selectedFields = numericFields.filter(
    field => viewSettings.selectedFieldNames[field.name]
  );

  const sideways = selectedFields.length > 2 ? viewSettings.sideways : false;
  const allComponentIds = getChildComponentIds({
    components,
    workspaceId,
    selectedComponentId: componentId,
    includeAllDescendants: viewSettings.includeAllDescendants,
    includedComponentIds,
  });

  if (componentId && includedComponentIds.has(componentId)) {
    // To keep the order similar to Navigator.
    allComponentIds.unshift(componentId);
  }

  const errors =
    allComponentIds.length > SPIDER_COMPONENTS_LIMIT && !bypassLimit
      ? [
          nodeLimitError({
            itemCount: allComponentIds.length,
            limit: SPIDER_COMPONENTS_LIMIT,
            itemsType: ItemsType.COMPONENTS,
            formatNumber: value => formatNumber(value, 'default', locale),
            onProceedAnyway: () => dispatchAction(bypassLimitAction()),
            ...getNodeLimitErrorLabels(i18nLocale),
          }),
        ]
      : [];

  const relevantComponentIds = errors.length
    ? allComponentIds.slice(0, SPIDER_COMPONENTS_LIMIT)
    : allComponentIds;

  const legendRows = getLegendRows({
    sideways,
    relevantComponentIds,
    workspaceId,
    components,
    selectedFields,
  });

  const childAggregationFunction =
    // @ts-expect-error TS2368 -- aggregateChildValues used to be a boolean. we convert it here to handle boolean legacy values.
    viewSettings.aggregateChildValues === true
      ? 'sum'
      : !viewSettings.aggregateChildValues
        ? 'zero'
        : viewSettings.aggregateChildValues;
  const chartPoints = getChartPoints({
    components,
    relevantComponentIds,
    numericFields,
    isFlipped: sideways,
    childAggregationFunction,
    selectedFieldNames: viewSettings.selectedFieldNames,
    i18nLocale,
  });

  return {
    sideways,
    numericFields,
    selectedFields,
    componentId,
    chartPoints,
    legendRows,
    componentsCount: relevantComponentIds.length,
    focusedComponentId,
    errors,
  };
};
